import { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Activity, Shield, TrendingUp, Database, Clock, FileText,
  Layers, Radio, Map, Ship, Plane, Wifi, Settings, BarChart3,
  Target, CheckCircle, ArrowRight, ChevronRight,
  AlertCircle, X, Wallet, Globe, ScanSearch, Camera, Crosshair,
  Network, Search, Grid3x3, Sparkles
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiClient } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { formatDate } from '@/lib/utils'
import { OnboardingTour } from '@/components/OnboardingTour'
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'

interface QueryLog {
  id: string
  timestamp: string
  endpoint: string
  query?: string
  status: string
  response_time_ms?: number
  results_count?: number
}

interface ModuleCard {
  id: string
  name: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  href: string
  hex: string
  /** Module IDs gate on user.modules exactly like Layout.tsx's sidebar —
   * null means always accessible (mirrors Layout's own moduleId: null cards). */
  moduleId: string | null
}

interface ModuleCategory {
  name: string
  modules: ModuleCard[]
}

// Mirrors apps/web/src/components/Layout.tsx's navigation array module-for-
// module (same moduleId values, same gating) so "all modules active listed"
// here means the same set a user can already reach from the sidebar — not a
// second, drifting copy of the module list. Telescope Monitoring is
// deliberately absent — removed from the sidebar for everyone, so it's
// dropped here too rather than surfacing a module with nowhere to click from.
const MODULE_CATEGORIES: ModuleCategory[] = [
  {
    name: 'Cyber & Financial Crime',
    modules: [
      {
        id: 'breachint', name: 'BreachINT', icon: Shield, href: '/app/breach', hex: '#ef4444', moduleId: 'breachint',
        description: 'Profile usernames, emails, phones, gov IDs & domains against breach intelligence data',
      },
      {
        id: 'wallet-intel', name: 'Wallet Intelligence', icon: Wallet, href: '/app/wallet-intel', hex: '#00C8FF', moduleId: 'wallet-intel',
        description: 'Crypto wallet risk screening, indirect exposure tracing & SAR/STR regulatory export',
      },
      {
        id: 'cyber-terrorism', name: 'Cyber Terrorism', icon: Crosshair, href: '/app/cyber-terrorism', hex: '#f43f5e', moduleId: 'cyber-terrorism',
        description: 'Terrorism incident and threat-actor tracking (GTD, SATP)',
      },
    ],
  },
  {
    name: 'Compliance & Due Diligence',
    modules: [
      {
        id: 'profiler', name: 'Profiler', icon: ScanSearch, href: '/app/profiler', hex: '#6366f1', moduleId: 'profiler',
        description: 'Automated CDD/EDD due-diligence checks with structured risk scoring',
      },
    ],
  },
  {
    name: 'Social & OSINT Investigation',
    modules: [
      {
        id: 'x-investigation', name: 'X Investigation', icon: Layers, href: '/app/x-investigation', hex: '#3b82f6', moduleId: 'x-investigation',
        description: 'Deep-dive X/Twitter account, network & community investigation',
      },
      {
        id: 'social-stream', name: 'Social Intelligence', icon: Radio, href: '/app/social-stream', hex: '#06b6d4', moduleId: 'social-stream',
        description: 'Real-time cross-platform monitoring, keyword tracking, and narrative correlation',
      },
      {
        id: 'playground', name: 'Playground', icon: Network, href: '/app/playground', hex: '#a855f7', moduleId: 'playground',
        description: 'Graph-based entity linking and pivot investigation workspace',
      },
    ],
  },
  {
    name: 'Geospatial & Physical Intelligence',
    modules: [
      {
        id: 'geoint-wayback', name: 'GEOINT Wayback', icon: Map, href: '/app/geoint-wayback', hex: '#22c55e', moduleId: 'geoint-wayback',
        description: 'Historical satellite imagery for location-based investigations',
      },
      {
        id: 'maritime', name: 'Maritime Intel', icon: Ship, href: '/app/geoint-maritime', hex: '#14b8a6', moduleId: 'maritime',
        description: 'Vessel tracking and maritime intelligence via AIS',
      },
      {
        id: 'air-monitoring', name: 'Air Monitoring', icon: Plane, href: '/app/air-monitoring', hex: '#eab308', moduleId: 'air-monitoring',
        description: 'Real-time aircraft tracking and air-traffic intelligence',
      },
      {
        id: 'wifi-intelligence', name: 'WiFi Intelligence', icon: Wifi, href: '/app/wifi-intelligence', hex: '#f97316', moduleId: 'wifi-intelligence',
        description: 'WiFi network discovery and geolocation analysis',
      },
      {
        id: 'facial-recognition', name: 'Facial Recognition', icon: Camera, href: '/app/facial-recognition', hex: '#ec4899', moduleId: 'facial-recognition',
        description: 'Reverse image and facial-recognition search',
      },
    ],
  },
]

// Not module-gated (moduleId: null in Layout.tsx too) — the real-time
// intelligence globe, always reachable regardless of subscription tier.
const MONITOR_CARD: ModuleCard = {
  id: 'monitor', name: 'Monitor', icon: Globe, href: '/app/monitor', hex: '#00E5FF', moduleId: null,
  description: 'Live global threat feed — cyber, geopolitical, and infrastructure events as they happen',
}

// Deep links into each module's actual primary action, not just its landing
// page — a genuine "ease of interaction" shortcut, not a second module list.
interface QuickAction { label: string; href: string; icon: React.ComponentType<{ className?: string }>; hex: string }
const QUICK_ACTIONS: QuickAction[] = [
  { label: 'New Breach Search', href: '/app/breach', icon: Search, hex: '#ef4444' },
  { label: 'Screen a Wallet', href: '/app/wallet-intel/lookup', icon: Wallet, hex: '#00C8FF' },
  { label: 'Run CDD Check', href: '/app/profiler', icon: ScanSearch, hex: '#6366f1' },
  { label: 'Open Playground', href: '/app/playground', icon: Network, hex: '#a855f7' },
]

export default function Dashboard() {
  const { user, refreshUser } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [recentQueries, setRecentQueries] = useState<QueryLog[]>([])
  const [stats, setStats] = useState({
    totalQueries: 0,
    successfulQueries: 0,
    failedQueries: 0,
    avgResponseTime: 0,
    queriesToday: 0,
    queriesThisWeek: 0
  })
  const [loading, setLoading] = useState(true)
  const [activeView, setActiveView] = useState<'overview' | 'activity'>('overview')
  const [paymentStatus, setPaymentStatus] = useState<{ type: 'success' | 'canceled' | null; tier?: string }>({ type: null })

  const hasModuleAccess = (moduleId: string | null): boolean => {
    if (!moduleId) return true
    if (user?.role === 'admin') return true
    return (user?.modules || []).includes(moduleId)
  }

  // Same filtering logic as Layout.tsx's sidebar, applied per-category so a
  // category with zero accessible modules just doesn't render (rather than
  // showing an empty section header).
  const visibleCategories = useMemo(() => {
    if (!user) return []
    return MODULE_CATEGORIES
      .map((cat) => ({ ...cat, modules: cat.modules.filter((m) => hasModuleAccess(m.moduleId)) }))
      .filter((cat) => cat.modules.length > 0)
  }, [user])

  const totalModuleCount = MODULE_CATEGORIES.reduce((n, c) => n + c.modules.length, 0)
  const accessibleModuleCount = visibleCategories.reduce((n, c) => n + c.modules.length, 0)

  useEffect(() => {
    const paymentParam = searchParams.get('payment')
    const tierParam = searchParams.get('tier')
    if (paymentParam === 'success') {
      setPaymentStatus({ type: 'success', tier: tierParam || undefined })
      if (refreshUser) refreshUser()
      const next = new URLSearchParams(searchParams)
      next.delete('payment')
      next.delete('tier')
      setSearchParams(next, { replace: true })
    } else if (paymentParam === 'canceled') {
      setPaymentStatus({ type: 'canceled' })
      const next = new URLSearchParams(searchParams)
      next.delete('payment')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, refreshUser])

  useEffect(() => {
    if (user?.id) loadUserData()
  }, [user])

  const loadUserData = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getUserQueryHistory(user!.id, { limit: 100 }) as { queries?: QueryLog[], logs?: QueryLog[], total?: number }
      const queryLogs = response.queries || response.logs || []
      setRecentQueries(queryLogs)
      const total = queryLogs.length
      const successful = queryLogs.filter((q: QueryLog) => q.status === 'success').length
      const failed = queryLogs.filter((q: QueryLog) => q.status === 'error' || q.status === 'failed').length
      const avgResponseTime = queryLogs.reduce((sum: number, q: QueryLog) => sum + (q.response_time_ms || 0), 0) / total || 0
      const now = Date.now()
      const today = queryLogs.filter((q: QueryLog) => (now - new Date(q.timestamp).getTime()) < 86400000).length
      const thisWeek = queryLogs.filter((q: QueryLog) => (now - new Date(q.timestamp).getTime()) < 7 * 86400000).length
      setStats({ totalQueries: total, successfulQueries: successful, failedQueries: failed, avgResponseTime: Math.round(avgResponseTime), queriesToday: today, queriesThisWeek: thisWeek })
    } catch (error: any) {
      console.error('Failed to load dashboard data', error)
      if (error?.message?.includes('401') || error?.message?.includes('403')) return
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-7 h-7 rounded-full border-2 border-green-500 border-t-transparent animate-spin" />
          <p className="text-slate-500 text-sm">Loading workspace...</p>
        </div>
      </div>
    )
  }

  const usagePercentage = user?.request_limit && user.request_limit !== -1
    ? ((user.requests_used || 0) / user.request_limit) * 100
    : 0

  const getGreeting = () => {
    const h = new Date().getHours()
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
  }

  const getQueryActivityData = () => {
    const now = Date.now()
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(now - (6 - i) * 86400000)
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      const dayStart = new Date(date.setHours(0, 0, 0, 0)).getTime()
      const dayEnd = dayStart + 86400000
      const dayQ = recentQueries.filter(q => {
        const t = new Date(q.timestamp).getTime()
        return t >= dayStart && t < dayEnd
      })
      return {
        date: dateStr,
        queries: dayQ.length,
        successful: dayQ.filter(q => q.status === 'success').length,
        failed: dayQ.filter(q => q.status === 'error' || q.status === 'failed').length
      }
    })
  }

  const getSuccessFailureData = () => {
    const successful = recentQueries.filter(q => q.status === 'success').length
    const failed = recentQueries.filter(q => q.status === 'error' || q.status === 'failed').length
    const other = recentQueries.length - successful - failed
    return [
      { name: 'Success', value: successful, color: '#22c55e' },
      { name: 'Failed', value: failed, color: '#ef4444' },
      { name: 'Other', value: other, color: '#6b7280' }
    ].filter(item => item.value > 0)
  }

  const getEndpointUsageData = (): Array<{ name: string; value: number }> => {
    const counts: Record<string, number> = {}
    recentQueries.forEach(q => {
      const ep = q.endpoint.split('/').pop() || q.endpoint
      counts[ep] = (counts[ep] || 0) + 1
    })
    return Object.entries(counts)
      .map(([name, value]) => ({ name: name.length > 15 ? name.slice(0, 15) + '…' : name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }

  const getResponseTimeData = () => {
    const now = Date.now()
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(now - (6 - i) * 86400000)
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      const dayStart = new Date(date.setHours(0, 0, 0, 0)).getTime()
      const dayEnd = dayStart + 86400000
      const dayQ = recentQueries.filter(q => {
        const t = new Date(q.timestamp).getTime()
        return t >= dayStart && t < dayEnd && q.response_time_ms
      })
      const avgTime = dayQ.length > 0
        ? Math.round(dayQ.reduce((s, q) => s + (q.response_time_ms || 0), 0) / dayQ.length)
        : 0
      return { date: dateStr, avgTime }
    })
  }

  const tooltipStyle = {
    contentStyle: {
      backgroundColor: '#0c1015',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '8px',
      color: '#e5e7eb',
      fontSize: '12px'
    }
  }

  const StatusPill = ({ status }: { status: string }) => {
    const s = status.toLowerCase()
    const cls = s === 'success'
      ? 'bg-green-500/15 text-green-400'
      : s === 'error' || s === 'failed'
        ? 'bg-red-500/15 text-red-400'
        : 'bg-amber-500/15 text-amber-400'
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cls}`}>{status}</span>
  }

  const ModuleTile = ({ module }: { module: ModuleCard }) => (
    <Link to={module.href} data-tour={module.id === 'breachint' ? 'breachint' : undefined}>
      <div
        className="group flex items-start gap-4 bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 hover:border-white/[0.14] hover:bg-white/[0.05] transition-all cursor-pointer h-full"
        style={{ borderLeftColor: module.hex, borderLeftWidth: '3px' }}
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${module.hex}18`, border: `1px solid ${module.hex}30`, color: module.hex }}
        >
          <module.icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold text-sm group-hover:text-green-400 transition-colors">{module.name}</h3>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-white group-hover:translate-x-0.5 transition-all flex-shrink-0" />
          </div>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">{module.description}</p>
        </div>
      </div>
    </Link>
  )

  return (
    <div className="space-y-7 min-h-screen text-white">
      <OnboardingTour />

      {/* Payment notification */}
      {paymentStatus.type && (
        <div className={`flex items-center justify-between p-4 rounded-xl border ${
          paymentStatus.type === 'success' ? 'bg-green-500/10 border-green-500/20' : 'bg-amber-500/10 border-amber-500/20'
        }`}>
          <div className="flex items-center gap-3">
            {paymentStatus.type === 'success'
              ? <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              : <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />}
            <div>
              <p className={`font-semibold text-sm ${paymentStatus.type === 'success' ? 'text-green-400' : 'text-amber-400'}`}>
                {paymentStatus.type === 'success'
                  ? `Payment successful — ${paymentStatus.tier || 'subscription'} plan activated`
                  : 'Payment canceled. No charges were made.'}
              </p>
              {paymentStatus.type === 'success' && (
                <p className="text-slate-400 text-xs mt-0.5">Your subscription has been updated with immediate effect.</p>
              )}
            </div>
          </div>
          <button onClick={() => setPaymentStatus({ type: null })} className="text-slate-500 hover:text-slate-300 transition-colors p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Page header */}
      <div className="flex items-start justify-between pb-6 border-b border-white/[0.07]" data-tour="welcome">
        <div>
          <h1 className="text-2xl font-bold text-white">{getGreeting()}, {user?.first_name || 'Analyst'}.</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-sm text-slate-400">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse inline-block" />
              All systems operational
            </span>
            <span className="text-slate-700">·</span>
            <span className="text-slate-400 text-sm capitalize">{user?.subscription_tier || 'Freemium'} plan</span>
            <span className="text-slate-700">·</span>
            <span className="text-slate-400 text-sm">{accessibleModuleCount} of {totalModuleCount} modules active</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {user?.role === 'admin' && (
            <Link to="/app/admin">
              <Button size="sm" variant="outline" className="border-white/[0.1] text-slate-300 hover:text-white hover:bg-white/[0.06] text-sm">
                <Settings className="w-3.5 h-3.5 mr-1.5" />
                Admin Panel
              </Button>
            </Link>
          )}
          <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-1.5 text-xs text-green-400 font-medium">
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full" />
            Online
          </div>
        </div>
      </div>

      {/* KPI metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-tour="usage-stats">
        <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 hover:border-white/[0.12] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-400 text-sm">Subscription</span>
            <TrendingUp className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-2xl font-bold text-white capitalize">{user?.subscription_tier || 'Freemium'}</div>
          <div className="text-slate-500 text-xs mt-1">{user?.request_limit === -1 ? 'Unlimited credits' : `${user?.request_limit || 0} credits / month`}</div>
        </div>

        <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 hover:border-white/[0.12] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-400 text-sm">Credits Used</span>
            <Database className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-2xl font-bold text-white">
            {user?.requests_used || 0}
            <span className="text-slate-500 text-base font-normal ml-1.5">/ {user?.request_limit === -1 ? '∞' : user?.request_limit || 0}</span>
          </div>
          <Progress value={Math.min(usagePercentage, 100)} className="h-1 mt-3 bg-slate-800/80" />
          <div className="text-slate-500 text-xs mt-2">{user?.request_limit !== -1 ? `${Math.round(usagePercentage)}% consumed` : 'No limit'}</div>
        </div>

        <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 hover:border-white/[0.12] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-400 text-sm">Total Queries</span>
            <BarChart3 className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-2xl font-bold text-white">{stats.totalQueries}</div>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-green-400 text-xs flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />{stats.successfulQueries} ok
            </span>
            {stats.failedQueries > 0 && (
              <span className="text-red-400 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />{stats.failedQueries} failed
              </span>
            )}
          </div>
        </div>

        <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5 hover:border-white/[0.12] transition-colors">
          <div className="flex items-center justify-between mb-4">
            <span className="text-slate-400 text-sm">Avg Response</span>
            <Clock className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-2xl font-bold text-white">{stats.avgResponseTime}<span className="text-slate-500 text-sm font-normal ml-1">ms</span></div>
          <div className="text-slate-500 text-xs mt-1">{stats.queriesThisWeek} queries this week</div>
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-slate-500" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {QUICK_ACTIONS.filter((a) => hasModuleAccess(MODULE_CATEGORIES.flatMap(c => c.modules).find(m => a.href.startsWith(m.href))?.moduleId ?? null)).map((action) => (
            <Link key={action.label} to={action.href}>
              <div className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.07] rounded-xl px-4 py-3.5 hover:border-white/[0.14] hover:bg-white/[0.05] transition-all cursor-pointer">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${action.hex}18`, color: action.hex }}>
                  <action.icon className="w-4 h-4" />
                </div>
                <span className="text-sm text-white font-medium truncate">{action.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Monitor — always-on live feed, called out separately from the gated module grid */}
      <Link to={MONITOR_CARD.href}>
        <div className="group flex items-center gap-5 rounded-xl p-5 border cursor-pointer transition-all"
          style={{ background: 'linear-gradient(135deg, #00E5FF12, transparent 60%)', borderColor: '#00E5FF30' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#00E5FF20', border: '1px solid #00E5FF40', color: '#00E5FF' }}>
            <Globe className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold text-sm">Monitor</h3>
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold" style={{ color: '#00E5FF' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" /> Live
              </span>
            </div>
            <p className="text-slate-400 text-xs mt-0.5">{MONITOR_CARD.description}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white group-hover:translate-x-0.5 transition-all flex-shrink-0" />
        </div>
      </Link>

      {/* Main content */}
      <Tabs value={activeView} onValueChange={(v) => setActiveView(v as any)}>
        <TabsList className="bg-white/[0.03] border border-white/[0.07] p-1 rounded-lg h-auto gap-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white/[0.08] data-[state=active]:text-white text-slate-400 rounded-md text-sm px-4 py-1.5 transition-all">
            Overview
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-white/[0.08] data-[state=active]:text-white text-slate-400 rounded-md text-sm px-4 py-1.5 transition-all">
            Full Activity Log
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="space-y-8 mt-6">

          {/* All modules, categorized — the dashboard's primary job is being a
              launcher, so this is shown in full rather than tucked behind a
              second tab or truncated to a handful of "quick access" cards. */}
          <div data-tour="modules">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Grid3x3 className="w-4 h-4 text-slate-500" />
                Your Modules
              </h2>
              <span className="text-slate-500 text-xs">{accessibleModuleCount} active</span>
            </div>
            {visibleCategories.length > 0 ? (
              <div className="space-y-6">
                {visibleCategories.map((cat) => (
                  <div key={cat.name}>
                    <h3 className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-3">{cat.name}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {cat.modules.map((module) => <ModuleTile key={module.id} module={module} />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-8 text-center">
                <p className="text-slate-400 text-sm">No modules enabled on your account yet.</p>
                <p className="text-slate-600 text-xs mt-1">Contact your organization admin or upgrade your plan to unlock modules.</p>
              </div>
            )}
          </div>

          {/* Summary stats row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5">
              <p className="text-slate-400 text-sm mb-1">Today</p>
              <div className="text-3xl font-bold text-white">{stats.queriesToday}</div>
              <p className="text-slate-600 text-xs mt-1">queries executed</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5">
              <p className="text-slate-400 text-sm mb-1">This Week</p>
              <div className="text-3xl font-bold text-white">{stats.queriesThisWeek}</div>
              <p className="text-slate-600 text-xs mt-1">total queries</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5">
              <p className="text-slate-400 text-sm mb-1">Success Rate</p>
              <div className="text-3xl font-bold text-green-400">
                {stats.totalQueries > 0 ? Math.round((stats.successfulQueries / stats.totalQueries) * 100) : 0}%
              </div>
              <p className="text-slate-600 text-xs mt-1">of all queries</p>
            </div>
          </div>

          {/* Charts 2×2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-5 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-500" />
                Query Activity — Last 7 Days
              </h3>
              {recentQueries.length > 0 ? (
                <ResponsiveContainer width="100%" height={210}>
                  <AreaChart data={getQueryActivityData()}>
                    <defs>
                      <linearGradient id="gQueries" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="date" stroke="#374151" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis stroke="#374151" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <Tooltip {...tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '11px', color: '#6b7280' }} />
                    <Area type="monotone" dataKey="queries" stroke="#22c55e" fill="url(#gQueries)" name="Total" strokeWidth={2} />
                    <Area type="monotone" dataKey="successful" stroke="#10b981" fill="#10b98115" name="Success" strokeWidth={1.5} />
                    <Area type="monotone" dataKey="failed" stroke="#ef4444" fill="#ef444415" name="Failed" strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[210px] text-slate-600 text-sm">No data yet</div>
              )}
            </div>

            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-5 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-slate-500" />
                Query Status Distribution
              </h3>
              {recentQueries.length > 0 ? (
                <ResponsiveContainer width="100%" height={210}>
                  <PieChart>
                    <Pie data={getSuccessFailureData()} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={75} dataKey="value">
                      {getSuccessFailureData().map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[210px] text-slate-600 text-sm">No data yet</div>
              )}
            </div>

            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-5 flex items-center gap-2">
                <Target className="w-4 h-4 text-slate-500" />
                Top Endpoints
              </h3>
              {recentQueries.length > 0 && getEndpointUsageData().length > 0 ? (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={getEndpointUsageData()} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis type="number" stroke="#374151" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis type="category" dataKey="name" stroke="#374151" tick={{ fontSize: 11, fill: '#6b7280' }} width={110} />
                    <Tooltip {...tooltipStyle} />
                    <Bar dataKey="value" fill="#22c55e" fillOpacity={0.75} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[210px] text-slate-600 text-sm">No data yet</div>
              )}
            </div>

            <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-5 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Response Time — Last 7 Days
              </h3>
              {recentQueries.length > 0 ? (
                <ResponsiveContainer width="100%" height={210}>
                  <LineChart data={getResponseTimeData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="date" stroke="#374151" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis stroke="#374151" tick={{ fontSize: 11, fill: '#6b7280' }} label={{ value: 'ms', angle: -90, position: 'insideLeft', style: { fill: '#4b5563', fontSize: 11 } }} />
                    <Tooltip {...tooltipStyle} formatter={(v: any) => [`${v}ms`, 'Avg Response']} />
                    <Line type="monotone" dataKey="avgTime" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[210px] text-slate-600 text-sm">No data yet</div>
              )}
            </div>
          </div>

          {/* Recent activity */}
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl overflow-hidden" data-tour="activity">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.05]">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-slate-500" />
                Recent Activity
              </h3>
              <button onClick={() => setActiveView('activity')} className="text-slate-500 hover:text-white text-xs flex items-center gap-1 transition-colors">
                View all <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            {recentQueries.length > 0 ? (
              <div className="divide-y divide-white/[0.04]">
                {recentQueries.slice(0, 6).map((q) => (
                  <div key={q.id} className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.02] transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <code className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded font-mono truncate max-w-[220px]">{q.endpoint}</code>
                        <StatusPill status={q.status} />
                      </div>
                      {q.query && <p className="text-slate-500 text-xs truncate max-w-sm">{q.query}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-slate-500 text-xs">{formatDate(q.timestamp)}</div>
                      {q.response_time_ms && <div className="text-slate-600 text-[11px] mt-0.5">{q.response_time_ms}ms</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-white/[0.04] border border-white/[0.07] flex items-center justify-center mb-4">
                  <FileText className="w-5 h-5 text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium mb-1">No activity yet</p>
                <p className="text-slate-600 text-sm mb-5">Run your first query to see activity here.</p>
                <Link to="/app/breach">
                  <Button size="sm" className="bg-green-500 hover:bg-green-600 text-black font-semibold">Start First Query</Button>
                </Link>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Full Activity Log ── */}
        <TabsContent value="activity" className="space-y-5 mt-6">
          <div>
            <h2 className="text-base font-semibold text-white mb-1">Query History</h2>
            <p className="text-slate-500 text-sm mb-5">Complete log of all investigation queries and results</p>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/[0.05] bg-white/[0.02]">
              <div className="col-span-4 text-xs text-slate-600 font-medium uppercase tracking-wider">Endpoint</div>
              <div className="col-span-3 text-xs text-slate-600 font-medium uppercase tracking-wider">Query</div>
              <div className="col-span-2 text-xs text-slate-600 font-medium uppercase tracking-wider">Status</div>
              <div className="col-span-2 text-xs text-slate-600 font-medium uppercase tracking-wider text-right">Timestamp</div>
              <div className="col-span-1 text-xs text-slate-600 font-medium uppercase tracking-wider text-right">ms</div>
            </div>
            {recentQueries.length > 0 ? (
              <div className="divide-y divide-white/[0.04]">
                {recentQueries.map((q) => (
                  <div key={q.id} className="flex md:grid md:grid-cols-12 gap-2 md:gap-4 px-5 py-3 hover:bg-white/[0.02] transition-colors items-center flex-wrap">
                    <div className="col-span-4">
                      <code className="text-xs text-green-400 bg-green-500/10 px-2 py-0.5 rounded font-mono truncate block max-w-full">{q.endpoint}</code>
                    </div>
                    <div className="col-span-3">
                      <span className="text-slate-400 text-xs truncate block">{q.query || '—'}</span>
                    </div>
                    <div className="col-span-2">
                      <StatusPill status={q.status} />
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="text-slate-500 text-xs">{formatDate(q.timestamp)}</span>
                    </div>
                    <div className="col-span-1 text-right">
                      <span className="text-slate-600 text-xs">{q.response_time_ms ?? '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 rounded-full bg-white/[0.04] border border-white/[0.07] flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
                <p className="text-slate-300 font-medium mb-1">No query history</p>
                <p className="text-slate-500 text-sm mb-6">Run an investigation to populate your activity log.</p>
                <Link to="/app/breach">
                  <Button size="sm" className="bg-green-500 hover:bg-green-600 text-black font-semibold">Start Your First Query</Button>
                </Link>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
